<template>
  <v-row justify="end" id="ticket-scale-summary" class="pa-1">
    <v-col cols="auto">
      <span class="subtitle-1">
        {{ title }}
      </span>
    </v-col>

    <v-col v-if="$vuetify.breakpoint.xl && scaleData">
      <TicketScaleDataInfoAsChips :scaleData="scaleData" :scaleDataSource="volumeSummary.scaleDataSource" :ext2="ticket.extTicketNumber2" />
    </v-col>

    <v-col v-else>
      <Icon
        icon="mdi-information-outline"
        :small="false"
        :tooltipText="$t('details')"
        @icon-clicked="infoIconClicked"
      />
    </v-col>

    <v-col cols="auto" v-if="$vuetify.breakpoint.lgAndUp">
      <Icon
        v-if="volumeSummary.scaleDataSource === ScaleDataSource.Expansion.value && userAssignedClaim(UserClaims.TicketManager)"
        icon="mdi-alpha-e-circle-outline"
        iconColor="primary"
        :tooltipText="$t('reExpandTicket')"
        @icon-clicked="reExpandTicket"/>
      <Icon
        :icon="printerWithScaleRuler.path"
        iconColor="primary"
        :tooltipText="$t('printScaleSheet')"
        @icon-clicked="printScaleSheet"/>
      <v-icon @click="summaryDisplayButtonClicked">{{ getSummaryChevron() }}</v-icon>
    </v-col>

    <v-col cols="12" v-if="ticket.unloadedAt || ticket.unloadedByUser" class="py-0">
      <template v-if="ticket.unloadedAt && ticket.unloadedByUser">
        {{ $t('unloadedByUserAt', { name: ticket.unloadedByUser, date: utcToLocalDate(ticket.unloadedAt, 'L - LT') }) }}
      </template>
    </v-col>

    <v-col v-if="volumeSummary.ticketVolumes" cols="12" id="scale-summary-table" :class="getSummaryTableClass()" :style="getSummaryTableStyle()">
      <DataTable
      :containerClass="'mt-n8 px-1'"
      smallHeaders
      :showSearch="false"
      :headers="scaleSummaryHeaders"
      :items="volumeSummary.ticketVolumes"
      :customCells="customCells">
        <template #grossVolume="{item}">
          {{ numberWithCommas(item.grossVolume, 3) }}
        </template>
        <template #netVolume="{item}">
          {{ numberWithCommas(item.netVolume, 3) }}
        </template>
        <template #scaleDataSource="{ }">
          <Icon
            :icon="getScaleDataSourceIcon(volumeSummary.scaleDataSource)"
            iconColor="secondary"
            :tooltipText="getScaleDataSourceTooltip(volumeSummary.scaleDataSource)"
            :small="false"
          />
        </template>
        <template #body.append>
          <tr class="font-weight-bold">
            <td class="text-lg-left">Totals</td>
            <td class="text-right">{{ numberWithCommas(volumeSummary.grossVolume, 3) }}</td>
            <td class="text-right">{{ numberWithCommas(volumeSummary.netVolume, 3) }}</td>
            <td class="text-left">-</td>
          </tr>
        </template>
      </DataTable>
    </v-col>

    <v-col v-if="displayTicketScaleData && scaleData && rateSheets.size > 0">
      <span class="subtitle-1">
        {{ $t('summaryByActivity') }}
      </span>
    </v-col>

    <v-col cols="12" v-if="activitySummaries.length > 0">
      <v-expansion-panels
        class="narrow-expansion-panels"
        accordion
        v-model="openActivityPanel">
        <v-expansion-panel
          v-for="activity in activitySummaries"
          :key="activity.key">
          <v-expansion-panel-header>
            <v-row no-gutters align="center" justify="space-between">
              <v-col cols="auto">
                <span class="subtitle-2">{{ activity.activityName }} - {{ activity.rateSheetDefinitionName }} ({{ activity.counterpartyName }})</span>
              </v-col>
              <v-col cols="auto" class="mr-4">
                <span class="subtitle-2">{{ formatMoney(activity.totalAmount) }}</span>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="grey lighten-4">
            <v-data-table
              dense
              class="small-headers mt-2"
              :headers="rateSheetLineTableHeaders"
              :items="activity.lineDetails"
              hide-default-footer
              :items-per-page="-1">
              <template #item.speciesGroup="{ item }">
                {{ item.speciesGroup }}
              </template>
              <template #item.lengthGroup="{ item }">
                {{ item.lengthGroup }}
              </template>
              <template #item.diameterGroup="{ item }">
                {{ item.diameterGroup }}
              </template>
              <template #item.grade="{ item }">
                {{ item.grade }}
              </template>
              <template #item.rate="{ item }">
                <v-row no-gutters align="center" justify="end">
                  <v-col cols="auto" v-if="item.isBaseRate">
                    <Icon
                      icon="baseRateMop"
                      iconColor="warning"
                      :tooltipText="$t('baseRateUsed')"
                      :small="false"
                      margin="mr-1"
                    />
                  </v-col>
                  <v-col cols="auto">
                    {{ formatMoney(item.rate) }}
                  </v-col>
                </v-row>
              </template>
              <template #item.grossVolume="{ item }">
                {{ numberWithCommas(item.grossVolume, 3) }}
              </template>
              <template #item.netVolume="{ item }">
                {{ numberWithCommas(item.netVolume, 3) }}
              </template>
              <template #item.amount="{ item }">
                {{ formatMoney(item.amount) }}
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>

    <v-col cols="12" v-if="displayTicketScaleData && scaleData && rateSheets.size > 0">
      <TicketScaleData
      :rateSheets="rateSheets"
      :scaleData="scaleData"
      :ticket="ticket"
      :financials="financials"
      :scaleDataSource="volumeSummary.scaleDataSource"
      />
    </v-col>

    <Dialog
    v-if="scaleInfoDialog"
    :stateId="dialogId"
    @close="dialogClosed"
    @click:outside="dialogClosed"
    @dialog-closing="dialogClosed">
      <TicketScaleDataInfoCard
        v-if="scaleInfoDialog"
        :scaleData="scaleData"
        :scaleDataSource="volumeSummary.scaleDataSource"
        :ext2="ticket.extTicketNumber2"
      />
    </Dialog>

    <Dialog
    v-if="scaleSheetFormatDialog"
    :stateId="formatDialogId"
    @close="closeFormatDialog"
    @click:outside="closeFormatDialog"
    @dialog-closing="closeFormatDialog">
      <RadioOptionDialog
        v-if="scaleSheetFormatDialog"
        :options="['csv', 'pdf']"
        :title="$t('printScaleSheet')"
        :submitButtonText="$t('download')"
        @close-dialog="closeFormatDialog"
        @optionChosen="onScaleSheetFormatConfirm"
      />
    </Dialog>
  </v-row>
</template>

<script>
import { mapActions } from 'vuex'
import { ScaleDataSource, UserClaims } from '@/utils/Enumerations'
import { numberWithCommas, formatMoney, mbfStringFromBf } from '@/utils/NumericMutations'
import { uniq } from 'lodash'
import { printerWithScaleRuler } from '@/images/AngelSvgLibrary'
import { exportTicketScaleSheetCSV } from '@/utils/csv/CSVExports'
import { generateScaleSheetPdf } from '@/utils/pdf-formatters/ScaleSheet'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { utcToLocalDate } from '@/utils/DateFormatter'

export default {
  name: 'TicketScaleSummary',

  components: {
    TicketScaleData: () => import('@/components/ticket/ticket-details/scale-data/TicketScaleData.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    TicketScaleDataInfoAsChips: () => import('@/components/ticket/ticket-details/scale-data/TicketScaleDataInfoAsChips.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    TicketScaleDataInfoCard: () => import('@/components/ticket/ticket-details/scale-data/TicketScaleDataInfoCard.vue'),
    RadioOptionDialog: () => import('@/components/helper/RadioOptionDialog.vue')
  },

  props: {
    ticket: {
      type: Object,
      required: true
    },
    financials: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    volumeSummary: [],
    displaySummary: true,
    displayTicketScaleData: false,
    scaleData: null,
    rateSheets: new Map(),
    activitySummaries: [],
    openActivityPanel: 0,
    printerWithScaleRuler,
    ScaleDataSource,
    UserClaims,
    scaleInfoDialog: false,
    scaleSheetFormatDialog: false,
    dialogId: uniqueDialogId('ticket-scale-summary'),
    formatDialogId: uniqueDialogId('ticket-scale-sheet-format')
  }),

  computed: {
    title () {
      return this.$t('summary')
    },

    scaleSummaryHeaders () {
      return [
        { text: this.$t('species'), value: 'species' },
        { text: this.$t('grossVolume'), value: 'grossVolume', align: 'right' },
        { text: this.$t('netVolume'), value: 'netVolume', align: 'right' },
        { text: this.$t('deck'), value: 'deck' }
      ]
    },

    customCells () {
      return [
        {
          slotName: 'grossVolume',
          value: 'grossVolume'
        },
        {
          slotName: 'netVolume',
          value: 'netVolume'
        },
        {
          slotName: 'scaleDataSource',
          value: 'scaleDataSource'
        }
      ]
    },

    ticketScaleSummaryIsDisplayed () {
      return this.$vuetify.breakpoint.lgAndUp ? this.displaySummary : true
    },

    rateSheetLineTableHeaders () {
      return [
        { text: this.$t('speciesGroup'), value: 'speciesGroup' },
        { text: this.$t('lengthGroup'), value: 'lengthGroup' },
        { text: this.$t('diameterGroup'), value: 'diameterGroup' },
        { text: this.$t('grade'), value: 'grade' },
        { text: this.$t('rate'), value: 'rate', align: 'right' },
        { text: this.$t('grossVolume'), value: 'grossVolume', align: 'right' },
        { text: this.$t('netVolume'), value: 'netVolume', align: 'right' },
        { text: this.$t('amount'), value: 'amount', align: 'right' }
      ]
    },

    activityRateMap () {
      return new Map(
        [
          ...(this.financials?.payables?.filter(item => item.deductFromPayableId == null) || []),
          ...(this.financials?.receivables || []),
          ...(this.financials?.journalEntries || [])
        ]
          .map(item => [item.activityId, { rate: item.computedAtRate, counterpartyName: item.accountName }])
      )
    }
  },

  watch: {
  },

  async mounted () {
    await this.init()
  },

  methods: {
    ...mapActions('scale-data', ['fetchVolumeSummaryForTicket', 'fetchScaleDataForTicket']),
    ...mapActions('rate-sheet', ['fetchRateSheet']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    numberWithCommas,
    formatMoney,
    mbfStringFromBf,
    userAssignedClaim,
    utcToLocalDate,

    async init () {
      this.volumeSummary = await this.fetchVolumeSummaryForTicket(this.ticket.ticketId)

      this.displayTicketScaleData = this.volumeSummary.scaleDataSource === ScaleDataSource.Scaled.value || this.volumeSummary.scaleDataSource === ScaleDataSource.Expansion.value || this.volumeSummary.scaleDataSource === ScaleDataSource.Imported.value

      if (this.displayTicketScaleData) {
        await this.loadScaleDataAndRateSheets()
      }
    },

    async loadScaleDataAndRateSheets () {
      this.scaleData = await this.fetchScaleDataForTicket(this.ticket.ticketId)
      await this.retrieveRateSheets()
      this.buildActivitySummaries()
    },

    async retrieveRateSheets () {
      const uniqRateSheetIds = uniq(this.scaleData.segmentVolumes
        .flatMap(s => s.amounts
          .flatMap(a => a.rateSheetId)))
        .filter(id => !!id && id > 0)

      await Promise.all(uniqRateSheetIds.map(id => this.fetchRateSheetById(id)))
    },

    async fetchRateSheetById (id) {
      if (id == null) return
      const rateSheet = await this.fetchRateSheet(id)
      this.rateSheets.set(id, rateSheet)
    },

    buildActivitySummaries () {
      const activityMap = new Map()
      const logHitsByLogNumber = new Map()

      for (const segment of this.scaleData.segmentVolumes) {
        const segmentHits = this.collectSegmentHits(segment, activityMap)

        for (const lineHit of segmentHits) {
          this.addSegmentVolumeToLineHit(lineHit, segment)
          this.registerLogHit(logHitsByLogNumber, segment.logNumber, lineHit)
        }
      }

      for (const segment of this.scaleData.segmentVolumes) {
        if ((segment.amounts || []).length > 0) continue

        const logHits = Array.from(logHitsByLogNumber.get(segment.logNumber)?.values() || [])
        for (const lineHit of logHits) {
          this.addSegmentVolumeToLineHit(lineHit, segment)
        }
      }

      this.activitySummaries = Array.from(activityMap.values()).map(activity => this.mapActivitySummary(activity))
    },

    collectSegmentHits (segment, activityMap) {
      const segmentHits = []

      for (const amount of segment.amounts || []) {
        const activityEntry = this.getOrCreateActivityEntry(activityMap, amount)
        activityEntry.totalAmount += amount.amount

        const lineHit = this.getOrCreateLineHit(activityEntry, amount.rateSheetLineId)
        lineHit.amount += amount.amount
        segmentHits.push(lineHit)
      }

      return segmentHits
    },

    getOrCreateActivityEntry (activityMap, amount) {
      const activityKey = `${amount.activityId}-${amount.rateSheetId}`
      if (!activityMap.has(activityKey)) {
        const rateSheet = this.rateSheets.get(amount.rateSheetId)
        activityMap.set(activityKey, {
          key: activityKey,
          activityId: amount.activityId,
          activityName: amount.activity,
          counterpartyName: this.activityRateMap.get(amount.activityId)?.counterpartyName || '',
          baseRate: this.activityRateMap.get(amount.activityId)?.rate,
          rateSheetId: amount.rateSheetId,
          rateSheetDefinitionName: rateSheet?.name || '',
          totalAmount: 0,
          lineHits: new Map()
        })
      }

      return activityMap.get(activityKey)
    },

    getOrCreateLineHit (activityEntry, rateSheetLineId) {
      if (!activityEntry.lineHits.has(rateSheetLineId)) {
        activityEntry.lineHits.set(rateSheetLineId, {
          key: `${activityEntry.key}-${rateSheetLineId}`,
          rateSheetLineId,
          grossVolume: 0,
          netVolume: 0,
          amount: 0,
          isBaseRate: rateSheetLineId === -1
        })
      }

      return activityEntry.lineHits.get(rateSheetLineId)
    },

    registerLogHit (logHitsByLogNumber, logNumber, lineHit) {
      if (!logHitsByLogNumber.has(logNumber)) {
        logHitsByLogNumber.set(logNumber, new Map())
      }

      logHitsByLogNumber.get(logNumber).set(lineHit.key, lineHit)
    },

    addSegmentVolumeToLineHit (lineHit, segment) {
      lineHit.grossVolume += segment.grossVolume
      lineHit.netVolume += segment.netVolume
    },

    mapActivitySummary (activity) {
      const rateSheet = this.rateSheets.get(activity.rateSheetId)
      const lineDetails = Array.from(activity.lineHits.values()).map(lineHit => {
        if (lineHit.isBaseRate) {
          return {
            ...lineHit,
            speciesGroup: null,
            lengthGroup: null,
            diameterGroup: null,
            grade: null,
            rate: activity.baseRate
          }
        }

        const rateSheetLine = rateSheet?.rateSheetLines?.find(
          rsl => rsl.rateSheetLineId === lineHit.rateSheetLineId
        )

        return {
          ...lineHit,
          speciesGroup: rateSheetLine?.speciesGroup?.label || null,
          lengthGroup: rateSheetLine?.lengthGroup?.label || null,
          diameterGroup: rateSheetLine?.diameterGroup?.label || null,
          grade: rateSheetLine?.grade?.name || null,
          rate: rateSheetLine?.rate || 0
        }
      })

      return {
        ...activity,
        lineDetails
      }
    },

    getScaleDataSourceIcon (scaleDataSource) {
      switch (scaleDataSource) {
        case ScaleDataSource.Scaled.value:
          return 'mdi-ruler-square'
        case ScaleDataSource.Expansion.value:
          return 'mdi-alpha-e-circle-outline'
        case ScaleDataSource.Imported.value:
          return 'mdi-file-import'
      }
    },

    getScaleDataSourceTooltip (scaleDataSource) {
      return ScaleDataSource.fromInt(scaleDataSource)
    },

    getSummaryChevron () {
      return this.ticketScaleSummaryIsDisplayed ? 'mdi-chevron-up' : 'mdi-chevron-down'
    },

    summaryDisplayButtonClicked () {
      this.displaySummary = !this.displaySummary
    },

    getSummaryTableClass () {
      return {
        visible: this.ticketScaleSummaryIsDisplayed
      }
    },

    getSummaryTableStyle () {
      return {
        height: this.ticketScaleSummaryIsDisplayed ? 'auto' : '0px'
      }
    },

    reExpandTicket () {
      this.$emit('re-expand-ticket')
    },

    printScaleSheet () {
      this.scaleSheetFormatDialog = true
      this.openOrUpdateDialog({ id: this.formatDialogId, width: '350px' })
    },

    async onScaleSheetFormatConfirm (format) {
      try {
        if (format === 'pdf') {
          await generateScaleSheetPdf(this.ticket)
        } else {
          await exportTicketScaleSheetCSV(this.ticket)
        }
      } finally {
        this.closeFormatDialog()
      }
    },

    closeFormatDialog () {
      this.scaleSheetFormatDialog = false
      this.closeDialogsAtOrAbove(this.formatDialogId)
    },

    infoIconClicked () {
      this.scaleInfoDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '500px' })
    },

    dialogClosed () {
      this.scaleInfoDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
    }
  }
}
</script>

<style>
#ticket-scale-summary #scale-summary-table {
  overflow: hidden;
  overflow-y: auto;
  transition: height 0.2s ease, opacity 0.2s ease;
}

#ticket-scale-summary #scale-summary-table.visible {
  opacity: 1;
}

#ticket-scale-summary #scale-summary-table:not(.visible) {
  height: 0;
  opacity: 0;
  overflow-y: hidden;
}
</style>
